import {
  IonAlert,
  IonButton,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonPage,
  IonSearchbar,
  IonSpinner
} from '@ionic/react';
import {
  bicycleOutline,
  locationOutline,
  navigateOutline,
  refreshOutline,
  storefrontOutline,
  timeOutline
} from 'ionicons/icons';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { buscarEmpresasProximas, requestService } from '../../../../../Utils/Services';
import {
  DeliveryTerms,
  readDeliveryAddress,
  saveDeliveryAddress,
  saveDeliveryTerms
} from '../../../../../Utils/deliveryAddressStorage';
import { resetEmpresaTheme } from '../../../../../Utils/empresaTheme';
import {
  buildNearbySearchBody,
  filterStoresBySpecialty,
  filterAndSortStores,
  NearbyStore,
  STORE_DISCOVERY_ALL_SPECIALTIES
} from '../../../../../Utils/storeDiscovery';
import { getStoreStatus } from '../../../../../Utils/storeStatus';
import Endereco from '../../../../Inicio/Endereco/Endereco';
import './LojaDiscovery.css';

type DiscoveryLocation = {
  endereco: string;
  bairro: string;
  cidade: string;
  latitude: number;
  longitude: number;
};

type DiscoveryResponse = {
  localizacao: DiscoveryLocation;
  especialidades: string[];
  lojas: NearbyStore[];
};

type Props = {
  onSelectStore: (storeId: string, deliveryTerms: DeliveryTerms) => Promise<boolean>;
};

const request = <T,>(url: string, config: any, history: any) => new Promise<T>((resolve, reject) => {
  requestService(url, config, history, resolve, reject, false);
});

const formatDistance = (store: NearbyStore) => {
  if (store.distanciaMetros === null || store.distanciaMetros === undefined) return '';
  if (store.distanciaMetros < 1000) return `${store.distanciaMetros} m`;
  return `${Number(store.distanciaKm || store.distanciaMetros / 1000).toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  })} km`;
};

const formatFee = (value: string | number) => {
  const rawValue = String(value ?? '0');
  const numericValue = typeof value === 'number'
    ? value
    : Number(rawValue.includes(',') ? rawValue.replace(/\./g, '').replace(',', '.') : rawValue) || 0;
  if (numericValue === 0) return 'Entrega grátis';
  return `Entrega R$ ${numericValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
};

const formatDeliveryTime = (value: string | number) => {
  const text = String(value ?? '').trim();
  if (!text) return '';
  return /min/i.test(text) ? text : `${text} min`;
};

const LojaDiscovery: React.FC<Props> = ({ onSelectStore }) => {
  const history = useHistory();
  const initialized = useRef(false);
  const latestStoreRequest = useRef(0);
  const addressInputRef = useRef<any>(null);
  const [location, setLocation] = useState<DiscoveryLocation | null>(null);
  const [stores, setStores] = useState<NearbyStore[]>([]);
  const [availableSpecialties, setAvailableSpecialties] = useState<string[]>([]);
  const [selectedSpecialty, setSelectedSpecialty] = useState(STORE_DISCOVERY_ALL_SPECIALTIES);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddressRequiredAlert, setShowAddressRequiredAlert] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectingStoreId, setSelectingStoreId] = useState('');

  const loadStores = async (body: Record<string, any>) => {
    const requestId = latestStoreRequest.current + 1;
    latestStoreRequest.current = requestId;
    setLoading(true);
    setError('');

    try {
      const response = await request<DiscoveryResponse>(buscarEmpresasProximas.url, {
        method: buscarEmpresasProximas.method,
        body: JSON.stringify(buildNearbySearchBody(body, STORE_DISCOVERY_ALL_SPECIALTIES)),
        credentials: 'include'
      }, history);

      if (requestId !== latestStoreRequest.current) return;

      const savedAddress = readDeliveryAddress();
      setLocation({
        ...response.localizacao,
        endereco: savedAddress.endereco || response.localizacao.endereco
      });
      setStores(Array.isArray(response.lojas) ? response.lojas : []);
      setAvailableSpecialties(Array.isArray(response.especialidades) ? response.especialidades : []);
      setLocationModalOpen(false);
    } catch (requestError: any) {
      if (requestId !== latestStoreRequest.current) return;
      setError(requestError?.message || 'Não foi possível encontrar lojas próximas.');
    } finally {
      if (requestId === latestStoreRequest.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    resetEmpresaTheme();

    const savedAddress = readDeliveryAddress();
    if (savedAddress.endereco
      && Number.isFinite(Number(savedAddress.latitude))
      && Number.isFinite(Number(savedAddress.longitude))) {
      loadStores({
        latitude: Number(savedAddress.latitude),
        longitude: Number(savedAddress.longitude)
      });
      return;
    }

    setLoading(false);
    setShowAddressRequiredAlert(true);
    // O carregamento inicial deve acontecer somente uma vez neste modo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredStores = useMemo(() => {
    return filterAndSortStores(filterStoresBySpecialty(stores, selectedSpecialty), search);
  }, [search, selectedSpecialty, stores]);

  const selectStore = async (store: NearbyStore) => {
    setSelectingStoreId(store.id);
    setError('');
    const selected = await onSelectStore(store.id, {
      taxa: String(store.taxaEntrega ?? ''),
      tempo: String(store.tempoEntrega ?? '')
    });
    if (!selected) {
      setError('Não foi possível abrir esta loja. Tente novamente.');
    }
    setSelectingStoreId('');
  };

  const completeAddress = (
    endereco: string,
    latitude: string,
    longitude: string,
    _taxa: string,
    _tempo: string,
    complemento: string
  ) => {
    saveDeliveryAddress({ endereco, latitude, longitude, complemento });
    saveDeliveryTerms('', '');
    setSelectedSpecialty(STORE_DISCOVERY_ALL_SPECIALTIES);
    setSearch('');
    setLocationModalOpen(false);
    setLocation({
      endereco,
      bairro: '',
      cidade: '',
      latitude: Number(latitude),
      longitude: Number(longitude)
    });
    loadStores({ latitude: Number(latitude), longitude: Number(longitude) });
  };

  const selectSpecialty = (specialty: string) => {
    if (specialty === selectedSpecialty) return;
    setSelectedSpecialty(specialty);
    setSearch('');
  };

  const specialtyOptions = [STORE_DISCOVERY_ALL_SPECIALTIES, ...availableSpecialties];

  return (
    <IonPage id="main-content-inicio" className="Inicio inicio-page store-discovery-page">
      <IonHeader className="ion-no-border store-discovery-header">
        <div className="store-discovery-location-row">
          <div className="store-discovery-location-icon">
            <IonIcon icon={locationOutline} />
          </div>
          <button className="store-discovery-location-button" onClick={() => setLocationModalOpen(true)}>
            <span>Entregar em</span>
            <strong>{location?.endereco || 'Informe seu endereço'}</strong>
          </button>
        </div>
        <div className="store-discovery-search-wrap">
          <IonSearchbar
            value={search}
            debounce={150}
            onIonChange={(event) => setSearch(event.detail.value || '')}
            placeholder={selectedSpecialty === STORE_DISCOVERY_ALL_SPECIALTIES
              ? 'Buscar lojas ou especialidades'
              : `Buscar lojas de ${selectedSpecialty}`}
          />
        </div>
      </IonHeader>

      <IonContent fullscreen>
        <main className="store-discovery-content">
          <section aria-label="Especialidades" className="store-discovery-specialties">
            {specialtyOptions.map((specialty) => (
              <button
                key={specialty}
                className={selectedSpecialty === specialty ? 'active' : ''}
                disabled={loading && selectedSpecialty === specialty}
                onClick={() => selectSpecialty(specialty)}
              >
                {specialty}
              </button>
            ))}
          </section>

          <section className="store-discovery-list-section">
            <div className="store-discovery-section-title">
              <div>
                <span>PERTO DE VOCÊ</span>
                <h1>Lojas que entregam no seu endereço</h1>
              </div>
              {location && <IonIcon icon={navigateOutline} />}
            </div>

            {loading ? (
              <div className="store-discovery-state">
                <IonSpinner name="crescent" color="primary" />
                <p>Buscando lojas próximas...</p>
              </div>
            ) : !location ? (
              <div className="store-discovery-state">
                <IonIcon icon={locationOutline} />
                <h2>Encontre lojas perto de você</h2>
                <p>Informe onde deseja receber o pedido para mostrarmos as lojas disponíveis.</p>
                <IonButton onClick={() => setLocationModalOpen(true)}>
                  Informar endereço
                </IonButton>
              </div>
            ) : error ? (
              <div className="store-discovery-state">
                <IonIcon icon={refreshOutline} />
                <h2>Não foi possível carregar</h2>
                <p>{error}</p>
                <IonButton onClick={() => location
                  ? loadStores({ latitude: location.latitude, longitude: location.longitude })
                  : setLocationModalOpen(true)}
                >
                  Tentar novamente
                </IonButton>
              </div>
            ) : filteredStores.length === 0 ? (
              <div className="store-discovery-state">
                <IonIcon icon={storefrontOutline} />
                <h2>Nenhuma loja encontrada</h2>
                <p>
                  {stores.length === 0
                    ? 'Ainda não há lojas que entregam neste endereço.'
                    : 'Nenhuma loja corresponde à sua busca.'}
                </p>
                {stores.length === 0 && (
                  <IonButton fill="outline" onClick={() => setLocationModalOpen(true)}>Trocar endereço</IonButton>
                )}
              </div>
            ) : (
              <div className="store-discovery-list">
                {filteredStores.map((store) => {
                  const status = getStoreStatus(store);
                  const isSelecting = selectingStoreId === store.id;
                  return (
                    <button
                      className={`store-discovery-card ${status.isOpen ? '' : 'closed'}`}
                      key={store.id}
                      disabled={Boolean(selectingStoreId)}
                      onClick={() => selectStore(store)}
                    >
                      <div className="store-discovery-logo">
                        {store.urlLogo
                          ? <img src={store.urlLogo} alt={`Logo ${store.nome}`} />
                          : <IonIcon icon={storefrontOutline} />}
                      </div>
                      <div className="store-discovery-card-content">
                        <div className="store-discovery-card-heading">
                          <h2>{store.nome}</h2>
                          {isSelecting && <IonSpinner name="crescent" />}
                        </div>
                        <p className="store-discovery-card-specialties">
                          {(store.especialidades || []).join(' • ') || 'Loja local'}
                        </p>
                        <div className="store-discovery-card-meta">
                          <span className={status.isOpen ? 'open' : 'closed-status'}>{status.label}</span>
                          {formatDistance(store) && <span>{formatDistance(store)}</span>}
                        </div>
                        <div className="store-discovery-card-delivery">
                          <span><IonIcon icon={bicycleOutline} />{formatFee(store.taxaEntrega)}</span>
                          {store.tempoEntrega !== '' && store.tempoEntrega !== null && (
                            <span><IonIcon icon={timeOutline} />{formatDeliveryTime(store.tempoEntrega)}</span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </main>
      </IonContent>

      <IonModal
        className="fullscreen-modal"
        isOpen={locationModalOpen}
        backdropDismiss
        onDidDismiss={() => setLocationModalOpen(false)}
      >
        <Endereco
          endereco={readDeliveryAddress().endereco}
          inputRef={addressInputRef}
          closeAction={completeAddress}
          closeActionSimple={() => setLocationModalOpen(false)}
          modoDescoberta
        />
      </IonModal>

      <IonAlert
        isOpen={showAddressRequiredAlert}
        onDidDismiss={() => setShowAddressRequiredAlert(false)}
        header="Precisamos do seu endereço"
        message="Informe onde deseja receber o pedido para localizarmos as lojas próximas que entregam até você."
        buttons={[
          {
            text: 'Informar endereço',
            handler: () => setLocationModalOpen(true)
          }
        ]}
      />
    </IonPage>
  );
};

export default LojaDiscovery;
